import {
  addCriteriaProperty,
  changePropertyRefinement,
  copySearchQuery,
  deleteSearchQuery,
  updateFieldsForProperty,
  updateSearchQuery,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import {
  selectCanDeleteSearchQuery,
  selectCriteriaProperties,
  selectEmptyCriteriaProperty,
  selectFieldsForProperty,
  selectHideProperty,
  selectIsViewMode,
  selectProperty,
  selectPropertyPlaceholder,
  selectPropertyRefinement,
} from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import noop from 'utils/noop';
import ActionButtons from '../actionButtons';
import CriteriaPropertyGroup from '../criteriaPropertyGroup';
import PropertyDropdown from '../propertyDropdown';
import { PropertyRefinementOptions } from '../propertyRefinementOptions';
import {
  CriteriaContainer,
  PropertyContainer,
  PropertyGroupsContainer,
  StyledCoreButton,
  StyledCoreDraggable,
  StyledDrag,
  StyledSegmentConnector,
} from './SearchQueryGroup.styled';
import { ElementIndexContextProvider, ElementType } from '../../contexts';

// type Props = {
//   /** The animation time in milliseconds. */
//   animationTime: number,
//   /** If the search query can be deleted. */
//   canDelete: boolean,
//   /** Array of properties to hide. */
//   hiddenProperties: Array<string>,
//   /** Array of properties to disable. */
//   disabledProperties: Array<string>,
//   /** List of criteria properties. */
//   criteriaProperties: [],
//   /** The value of an empty criteria property . */
//   emptyCriteriaProperty: {},
//   /** The fields of the property. */
//   fields: [],
//   /** pass filtered criteria properties to the criteria property group, a key/value pair of {[property]: [filteredCriteriaProperty]} */
//   filteredCriteriaProperties: [],
//   /**
//    * Callback function that returns the selected property to get fields for if fields do not exist.
//    *
//    * ```
//    * getFieldsForProperty(property: string) => {}
//    * ```
//    * @param { String } property: The property to get fields for.
//    * @returns { void }
//    */
//   getFieldsForProperty: (property: string) => {},
//   /** If the there are multiple search groups. */
//   hasMultipleGroups: boolean,
//   /** Hide the property dropdown. */
//   hideProperty: boolean,
//   /** If query builder is in view mode. */
//   isViewMode: boolean,
//   /** If query builder is in read only mode. */
//   isReadOnly?: boolean,
//   /** Disables animations. */
//   noAnimation: boolean,
//   /**
//    * Callback function when adding a criteria property.
//    *
//    * ```
//    * onAddCriteriaProperty(criteriaProperty, searchGroupIndex, searchQueryIndex) => {}
//    * ```
//    * @props { Object } criteriaProperty: The criteria property to add.
//    * @props { Number } searchGroupIndex: The index of the search group.
//    * @props { Number } searchQueryIndex: The index of the query group.
//    * @returns { void }
//    */
//   onAddCriteriaProperty: (
//     criteriaProperty: object,
//     searchGroupIndex: number,
//     searchQueryIndex: number,
//   ) => {},
//   /**
//    * Callback function when copying a search query.
//    *
//    * ```
//    * onCopySearchQuery(searchQueryIndex, searchGroupIndex) => {}
//    * ```
//    * @props { Number } searchQueryIndex: The index of the search query to copy.
//    * @props { Number } searchGroupIndex: The index of the search group the search query is in.
//    * @returns { void }
//    */
//   onCopySearchQuery: (searchQueryIndex: number, searchGroupIndex: number) => {},
//   /**
//    * Callback function when deleting a search query.
//    *
//    * ```
//    * onDeleteSearchQuery(searchQueryIndex, searchGroupIndex) => {}
//    * ```
//    * @props { Number } searchQueryIndex: The index of the search query to delete.
//    * @props { Number } searchGroupIndex: The index of the search group the search query is in.
//    * @returns { void }
//    */
//   onDeleteSearchQuery: (searchQueryIndex: number, searchGroupIndex: number) => {},
//   /** Value of selected property. */
//   property: string,
//   /** Placeholder for property dropdown. */
//   propertyPlaceholder: string,
//   /** The combinator of the search query. */
//   queryCombinator: string,
//   /** The index of the search group. */
//   searchGroupIndex: number,
//   /** The index of the search query. */
//   searchQueryIndex: number,
//   /** Store object to get state. (Used for connected CriteriaPropertyGroup component) */
//   store: {},
//   /** The total number of search queries. */
//   totalQueries: number,
//   /** Whether or not to display a cast type field for specific Long type criteria property fields */
//   useCastTypeField: boolean,
//   /** The properties to restrict to if specified. See usage in src/modules/queryBuilder/legacyQueryBuilder/components/propertyDropdown/index.js */
//   restrictToProperties?: Array<string>,
// };

class SearchQueryGroup extends React.Component {
  static defaultProps = {
    animationTime: 200,
    criteriaProperties: [],
    fields: [],
    noAnimation: false,
    getFieldsForProperty: noop,
    isReadOnly: false,
    onAddCriteriaProperty: noop,
    onCopySearchQuery: noop,
    onDeleteSearchQuery: noop,
    onUpdateFieldForProperty: noop,
    property: '',
    useCastTypeField: false,
  };

  constructor(props) {
    super(props);
    const { noAnimation, animationTime } = props;

    this.state = {
      animationTime: noAnimation ? 0 : animationTime,
      isDeleting: false,
    };
  }

  /**
   * handleAddNewCriteria
   * Callback function when adding a new criteria property.
   * @returns { void }
   */
  handleAddNewCriteria = () => {
    const { onAddCriteriaProperty, emptyCriteriaProperty, searchGroupIndex, searchQueryIndex } =
      this.props;

    // data type that was selected for the group
    // the next field will need to be initiated with this data type
    const dataType = this.props.property;

    // make sure the next field has the correct data type
    // based on what was selected for the group
    onAddCriteriaProperty(
      { ...emptyCriteriaProperty, dataType },
      searchGroupIndex,
      searchQueryIndex,
    );
  };

  /**
   * handleChangeProperty
   * Callback function when changing the property (dataType) of the searchQuery.
   * @param { Object } e: The change event.
   * @param { Object } props. The props of the property dropdown containing the updated value.
   * @returns { void }
   */
  handleChangeProperty = (e, { value }) => {
    const {
      getFieldsForProperty,
      onUpdateFieldForProperty,
      onUpdateSearchQuery,
      searchGroupIndex,
      searchQueryIndex,
    } = this.props;
    // updates queries to empty and update to new property
    onUpdateSearchQuery(
      {
        dataType: value,
        maxMatch: null,
        minMatch: null,
        searchCombo: {
          combinator: 'And',
          searchQueries: [{ comparatorType: '', dataType: value, field: '', fieldType: '' }],
        },
      },
      searchGroupIndex,
      searchQueryIndex,
    );

    const newFields = getFieldsForProperty(value);
    if (Array.isArray(newFields)) {
      onUpdateFieldForProperty(value, newFields);
    }
  };

  /**
   * handleChangePropertyRefinement
   * Callback function when changing the properties minMatch and maxMatch of the searchQuery.
   * @param { Number } minMatch: updated value for minMatch
   * @param { Number } maxMatch: updated value for maxMatch
   * @returns { void }
   */
  handleChangePropertyRefinement = (minMatch, maxMatch) => {
    const { onChangePropertyRefinement, searchGroupIndex, searchQueryIndex } = this.props;
    onChangePropertyRefinement(minMatch, maxMatch, searchGroupIndex, searchQueryIndex);
  };

  /**
   * handleCopySearchQuery
   * Callback function when copying a search query.
   * @returns { void }
   */
  handleCopySearchQuery = () => {
    const { onCopySearchQuery, searchGroupIndex, searchQueryIndex } = this.props;
    onCopySearchQuery(searchQueryIndex, searchGroupIndex);
  };

  /**
   * postDeleteSearchQuery
   * Callback function after delete action has been called. Calls onDeleteSearchQuery.
   * @returns { void }
   */
  postDeleteSearchQuery = () => {
    const { onDeleteSearchQuery, searchGroupIndex, searchQueryIndex } = this.props;
    this.setState({ isDeleting: false });
    onDeleteSearchQuery(searchQueryIndex, searchGroupIndex);
  };

  /**
   * handleDeleteSearchQuery
   * Callback function when delete button is clicked. Sets isDeleting state flag for delete animation.
   * @returns { void }
   */
  handleDeleteSearchQuery = () => {
    const { animationTime } = this.state;
    this.setState({ isDeleting: true });
    setTimeout(this.postDeleteSearchQuery, animationTime);
  };

  /**
   * renderCriteriaProperties
   * Generates the criteria property group components.
   * @returns { Array } The rendered criteria properties.
   */
  renderCriteriaProperties = () => {
    const {
      criteriaProperties,
      filteredFieldsByPropertyMap,
      searchGroupIndex,
      searchQueryIndex,
      store,
      useCastTypeField,
      isReadOnly,
      restrictToProperties,
      showFieldTextInput,
      type,
    } = this.props;
    const shouldValidateFields = !!restrictToProperties?.length;
    return criteriaProperties.map((property, index) => (
      <ElementIndexContextProvider
        key={`property-group-${index}`}
        index={index}
        type={ElementType.Condition}
      >
        <CriteriaPropertyGroup
          type={type}
          criteriaPropertyIndex={index}
          searchGroupIndex={searchGroupIndex}
          searchQueryIndex={searchQueryIndex}
          store={store}
          useCastTypeField={useCastTypeField}
          isReadOnly={isReadOnly}
          filteredFieldsByPropertyMap={filteredFieldsByPropertyMap}
          shouldValidateFields={shouldValidateFields}
          showFieldTextInput={showFieldTextInput}
          {...property}
        />
      </ElementIndexContextProvider>
    ));
  };

  render() {
    const {
      canDelete,
      hiddenProperties,
      hiddenPropertySections,
      disabledProperties,
      hasMultipleGroups,
      hideProperty,
      isViewMode,
      isReadOnly,
      property,
      propertyPlaceholder,
      propertyRefinement,
      queryCombinator,
      searchGroupIndex,
      searchQueryIndex,
      totalQueries,
      restrictToProperties,
      properties,
      canSetMaxPropertyCount,
    } = this.props;
    return (
      <StyledCoreDraggable
        isViewMode={isViewMode}
        draggingClass="cssDraggingClass"
        disabled={isViewMode || isReadOnly}
        id={`${property}-of-query-${searchQueryIndex}-group${searchGroupIndex}`}
        index={searchQueryIndex}
      >
        <StyledSegmentConnector
          isViewMode={isViewMode}
          isReadOnly={isReadOnly}
          hideConnectorLines={isViewMode}
          isSubGroupConnector={hasMultipleGroups}
          isLastConnector={searchQueryIndex === totalQueries - 1}
          isTopConnector={searchQueryIndex === 0}
          value={queryCombinator}
        />
        <CriteriaContainer
          data-test="search-query-grouping"
          isViewMode={isViewMode}
          isViewModeWithProperty={!hideProperty && isViewMode}
          queryCombinator={queryCombinator}
          data-role="search-query-group"
        >
          <PropertyContainer
            data-test="criteria-property-container"
            hideProperty={hideProperty}
            isViewMode={isViewMode}
          >
            {!isViewMode && <StyledDrag width={20} />}
            {!hideProperty && (
              <>
                {/*
                  Don't show refine options if:
                    1. No property is selected
                    2. contact property is selected
                  See: https://iterable.atlassian.net/browse/WF-6445
                */}
                {property && property !== 'user' && (
                  <PropertyRefinementOptions
                    isReadOnly={isReadOnly}
                    isViewMode={isViewMode}
                    handleChange={this.handleChangePropertyRefinement}
                    value={propertyRefinement}
                    canSetMaxPropertyCount={canSetMaxPropertyCount}
                  />
                )}
                <PropertyDropdown
                  isReadOnly={isReadOnly}
                  hiddenProperties={hiddenProperties}
                  hiddenPropertySections={hiddenPropertySections}
                  disabledProperties={disabledProperties}
                  isViewMode={isViewMode}
                  onChange={this.handleChangeProperty}
                  placeholder={propertyPlaceholder}
                  value={property}
                  restrictToProperties={restrictToProperties}
                  properties={properties}
                />
              </>
            )}
            {!isViewMode && !isReadOnly && (
              <ActionButtons
                className="cssActionButtons"
                canDelete={canDelete}
                isGroupActionButtons
                onCopy={this.handleCopySearchQuery}
                onDelete={this.handleDeleteSearchQuery}
                trashIconDelete
                copyIconOutline
              />
            )}
          </PropertyContainer>
          {(property || hideProperty) && (
            <PropertyGroupsContainer hideProperty={hideProperty}>
              {this.renderCriteriaProperties()}
              {!isViewMode && (
                <StyledCoreButton disabled={isReadOnly} onClick={this.handleAddNewCriteria} table>
                  Add Field
                </StyledCoreButton>
              )}
            </PropertyGroupsContainer>
          )}
        </CriteriaContainer>
      </StyledCoreDraggable>
    );
  }
}

export { SearchQueryGroup as SearchQueryGroupComponent };

const mapStateToProps = (state, props) => ({
  emptyCriteriaProperty: selectEmptyCriteriaProperty(state),
  fields: selectFieldsForProperty(state, props),
  hideProperty: selectHideProperty(state),
  isViewMode: selectIsViewMode(state),
  property: selectProperty(state, props),
  propertyPlaceholder: selectPropertyPlaceholder(state),
  propertyRefinement: selectPropertyRefinement(state, props),
  canDelete: selectCanDeleteSearchQuery(state, props),
  criteriaProperties: selectCriteriaProperties(state, props),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      onAddCriteriaProperty: addCriteriaProperty,
      onChangePropertyRefinement: changePropertyRefinement,
      onCopySearchQuery: copySearchQuery,
      onDeleteSearchQuery: deleteSearchQuery,
      onUpdateFieldForProperty: updateFieldsForProperty,
      onUpdateSearchQuery: updateSearchQuery,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(SearchQueryGroup);
